import axios from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import Keycloak from 'keycloak-js';
import { TroisJSVuePlugin } from 'troisjs';
import { createApp } from 'vue';
import VueApexCharts from 'vue3-apexcharts';
import { RouteLocationNormalized, NavigationGuardNext } from 'vue-router';

import App from './App.vue';
import Styling from './components/General/Styling.vue';
import StylingColors from './components/General/StylingColors.vue';
import ClickOutsideDirective from './directives/clickOutsideDirective';
import ResizableTableDirective from './directives/resizableTable';
import installComposables from './plugins/installComposables';
import posthogPlugin from './plugins/posthog';
import user from './plugins/user';
import router from './router';
import store from './store';
import '../public/assets/fontawesome/css/fontawesome.css';
import '../public/assets/fontawesome/css/regular.css';
import '../public/assets/fontawesome/css/solid.css';

interface KeycloakInitOptions {
  url: string;
  realm: string;
  clientId: string;
  silentCheckSsoFallback: boolean;
  checkLoginIframe: boolean;
  silentCheckSsoRedirectUri: string;
  onLoad: 'check-sso' | 'login-required';
}

interface RouteMeta {
  requireLogin?: boolean;
  requiredRole?: string[];
  requireStaff?: boolean;
  requireInternal?: boolean;
  title?: string;
}

interface UserState {
  is_staff: boolean;
  is_external: boolean;
  organization: {
    color: {
      primary_color: null;
      primary_text_color: null;
      accent_color: null;
      accent_text_color: null;
    };
    additional_cost_types: Record<string, unknown>;
  };
  profile_pic: string;
}

const initOptions: KeycloakInitOptions = {
  url: import.meta.env.VITE_KEYCLOAK_URL,
  realm: import.meta.env.VITE_KEYCLOAK_REALM,
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENTID,
  silentCheckSsoFallback: true,
  checkLoginIframe: true,
  silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso',
  onLoad: 'check-sso',
};

const setAuthState = (): void => {
  store.commit('setAuthenticated', keycloak.authenticated);
};

const performLogin = (destination: string): void => {
  const getUrl = window.location;
  let redirect = getUrl.protocol + '//' + getUrl.host;
  if (destination !== '/') {
    redirect = redirect + '?r=' + destination;
  }
  const domainParts = getUrl.hostname.split('.');
  const subdomain = domainParts[0] === 'www' ? domainParts[1] : domainParts[0];
  keycloak.login({ redirectUri: redirect, idpHint: subdomain });
};

const checkForRolemembership = (to: RouteLocationNormalized): boolean => {
  const requiredRoles = (to.meta as RouteMeta).requiredRole;
  const userState = store.state.user as UserState;

  if ((to.meta as RouteMeta).requireStaff === true && userState.is_staff === false) {
    return false;
  }

  if ((to.meta as RouteMeta).requireInternal === true && userState.is_external === true) {
    return false;
  }

  if (requiredRoles === undefined) {
    return true;
  }

  return requiredRoles.some((role: string) => keycloak.hasRealmRole(role));
};

const sendToRoute = (to: RouteLocationNormalized, next: NavigationGuardNext): void => {
  next();
};

const keycloak = new Keycloak(initOptions);

keycloak
  .init({ onLoad: initOptions.onLoad })
  .then(() => {
    axios.defaults.baseURL = !import.meta.env.PROD ? 'http://localhost' : document.location.origin;
    axios.defaults.withCredentials = true;
    const app = createApp(App);
    app.use(store);
    keycloak.onAuthSuccess = () => setAuthState();
    keycloak.onAuthRefreshError = function () {
      store.commit('removeTokens');
    };

    router.beforeEach((to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
      const DEFAULT_TITLE = '3D Spark';
      document.title = (to.meta as RouteMeta).title || DEFAULT_TITLE;

      if (to.matched.some(record => (record.meta as RouteMeta).requireLogin)) {
        if (!keycloak.authenticated) {
          setAuthState();
          if (keycloak.refreshToken === undefined) {
            performLogin(window.location.pathname);
          }
        } else if (keycloak.hasRealmRole('alstom-redirect') && window.location.host === 'platform.3dspark.de') {
          window.location.replace('https://designtoprint-alstom.3dspark.de');
        } else if (
          keycloak.hasRealmRole('unauthorized') &&
          !(keycloak.hasRealmRole('external') || keycloak.hasRealmRole('external_sso_provider'))
        ) {
          if (window.location.pathname !== '/new-user') {
            router.push('/new-user');
          }
        } else if (keycloak.hasRealmRole('unlicensed')) {
          if (window.location.pathname !== '/unlicensed') {
            router.push('/unlicensed');
          }
        } else if (checkForRolemembership(to)) {
          setAuthState();
          keycloak
            .updateToken(Number(import.meta.env.VITE_TIME_BEFORE_TOKEN_REFRESH))
            .then(function (refreshed: boolean) {
              if (refreshed) {
                setAuthState();
              }
              sendToRoute(to, next);
            })
            .catch((err: Error) => {
              console.error(err);
              if (keycloak.token === undefined) {
                keycloak.clearToken();
                router.push('/login');
              } else {
                keycloak.clearToken();
                router.push('/something-went-wrong');
              }
            });
        } else {
          router.push('/401');
        }
      } else {
        next();
      }
    });

    app.config.globalProperties.$keycloak = keycloak;
    app.use(user);
    app.config.globalProperties.$dayjs = dayjs;
    dayjs.extend(utc);
    app.use(router);
    app.use(TroisJSVuePlugin);
    app.use(VueApexCharts);
    app.component('Styling', Styling);
    app.component('StylingColors', StylingColors);
    app.use(posthogPlugin);

    // Set Color scheme globally, same as in assets/css/coloe_scheme -> chart palette
    (window as any).Apex.colors = ['#035e7b', '#007385', '#00867e', '#009766', '#50a443', '#97ab13'];

    app.use(installComposables);
    app.use(ClickOutsideDirective);
    app.use(ResizableTableDirective);
    app.mount('#app');
  })
  .catch((e: Error) => {
    console.log('Authentication Failed');
    console.log(e);
    keycloak.logout({ redirectUri: window.location.origin + '/login' });
  });
